import { List, Popover, Typography } from "@mui/material"
import FilterDropdownApplyButton from "components/FilterDropdown/FilterDropdownApplyButton"
import FilterDropdownButton from "components/FilterDropdown/FilterDropdownButton"
import FilterDropdownFilterItem from "components/FilterDropdown/FilterDropdownFilterItem"
import SearchBar from "components/SearchBar"
import { useBoolean } from "hooks/useBoolean"
import useSelect from "hooks/useSelect"
import React, { useRef, useState } from "react"
import { Filter } from "types/types"
import performFilterSearch from "utils/performFilterSearch"
import { makeStyles } from "tss-react/mui-compat"

const useStyles = makeStyles()(
  (theme) => ({
    popover: {
      overflowY: "hidden",
    },
    paper: {
      width: 300,
      height: "100%",
      maxHeight: 400,
      display: "flex",
      flexDirection: "column",
    },
    searchBar: {
      maxHeight: 64,
      minHeight: 64,
      padding: 7,
      "&.MuiInput-underline:before": {
        borderBottom: "none",
      },
    },
    list: {
      flex: 1,
      overflow: "scroll",
      "& .MuiCheckbox-colorPrimary": {
        color: "#000000",
      },
    },
    noFiltersFound: {
      padding: theme.spacing(1),
    },
    filterAll: {
      borderTop: "1.5px solid #D8D8D8",
    },
  })
)

export interface FilterDropdownProps {
  id: string
  list: Filter[]
  defaultSelectedList: Filter["value"][]

  onApply: (newSelectedList: Filter["value"][]) => void
}

export const FilterDropdown = (props: FilterDropdownProps) => {
  const { id, list, defaultSelectedList, onApply } = props
  const [internalSelectedList, setInternalSelectedList] = useState(() => [...defaultSelectedList])
  const { classes } = useStyles()
  const filterDropdownButtonRef = useRef<HTMLButtonElement>(null)

  const selectState = useSelect({
    list,
    selectedList: internalSelectedList,
    onChangeSelectedList: setInternalSelectedList,
  })
  const isMenuOpen = useBoolean(false)

  const [searchList, setSearchList] = useState<Filter[] | null>(null)

  const [searchValue, setSearchValue] = useState("")

  const createId = (string: string) => id + "-" + string

  const handleApply = () => {
    isMenuOpen.off()
    onApply([...internalSelectedList])
  }

  const onSearch = () => {
    setSearchList(performFilterSearch(list, searchValue))
  }

  const onClear = () => {
    setSearchList(null)
    setSearchValue("")
  }

  const handleOpenFilterDropdown = () => {
    setInternalSelectedList([...defaultSelectedList])
    isMenuOpen.on()
  }

  const popoverId = createId("popover")

  const filterList = searchList ?? list
  return (
    <>
      <FilterDropdownButton
        ref={filterDropdownButtonRef}
        isMenuOpen={isMenuOpen.value}
        openMenu={handleOpenFilterDropdown}
        closeMenu={isMenuOpen.off}
        ariaControls={isMenuOpen.value ? popoverId : undefined}
        appliedFiltersCount={defaultSelectedList.length}
      />
      <Popover
        id={popoverId}
        aria-hidden={isMenuOpen.value ? "true" : "false"}
        open={isMenuOpen.value}
        anchorEl={filterDropdownButtonRef.current}
        onClose={isMenuOpen.off}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        classes={{
          root: classes.popover,
          paper: classes.paper,
        }}>
        <SearchBar
          iconType={searchList ? "clear" : "search"}
          onSearch={onSearch}
          onEnter={onSearch}
          onClear={onClear}
          className={classes.searchBar}
          onChange={(newValue) => setSearchValue(newValue)}
          value={searchValue}
          placeholder="Search Filters"
        />
        <List dense disablePadding className={classes.list}>
          {filterList.length > 0 && (
            <FilterDropdownFilterItem
              className={classes.filterAll}
              labelId={createId("select-deselect-all")}
              isSelected={!selectState.canSelectAll}
              label={selectState.canSelectAll ? "Selected All" : "Deselected All"}
              indeterminate={selectState.isSomethingSelected}
              onClick={selectState.canSelectAll ? selectState.selectAll : selectState.deselectAll}
              divider
            />
          )}
          {filterList.length > 0 ? (
            filterList.map(({ label, value }) => {
              const labelId = createId("item-label-id-" + value)
              const isSelected = selectState.isSelected(value)
              return (
                <FilterDropdownFilterItem
                  key={value.toString()}
                  labelId={labelId}
                  label={label}
                  isSelected={isSelected}
                  onClick={() => selectState.select(value, isSelected)}
                />
              )
            })
          ) : (
            <Typography
              className={classes.noFiltersFound}
              variant="body2"
              component="p"
              align="center">
              No filters found!
            </Typography>
          )}
        </List>
        <FilterDropdownApplyButton
          onClick={handleApply}
          amountOfSelectedItems={selectState.amountOfSelectedItems}
        />
      </Popover>
    </>
  )
}
